import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";

const TEXTURE =
  "https://images.pexels.com/photos/12564248/pexels-photo-12564248.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function RegisterPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const res = await register(name, email, password);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Account created");
      const next = new URLSearchParams(location.search).get("next");
      navigate(next === "pricing" ? "/#pricing" : "/dashboard");
    } else {
      setError(res.error || "Registration failed");
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2" data-testid="register-page">
      <div className="flex items-center justify-center p-8 lg:p-16 order-2 md:order-1">
        <div className="w-full max-w-sm">
          <Link to="/" className="font-display text-xl font-medium mb-12 inline-block">
            14<span className="text-zinc-500">/</span>Brandscaling
          </Link>
          <h1 className="font-display text-4xl tracking-tighter font-medium mb-2">Create account</h1>
          <p className="text-zinc-500 mb-10">Start your 14-day brand sprint today.</p>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
            <div>
              <label className="block text-xs text-zinc-500 mb-2 font-mono uppercase tracking-wider">Name</label>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                data-testid="register-name-input"
              />
            </div>
            <div>
              <label className="block text-xs text-zinc-500 mb-2 font-mono uppercase tracking-wider">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@brand.com"
                data-testid="register-email-input"
              />
            </div>
            <div>
              <label className="block text-xs text-zinc-500 mb-2 font-mono uppercase tracking-wider">Password</label>
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                data-testid="register-password-input"
              />
            </div>
            {error && (
              <div className="text-sm text-red-400 border border-red-900/40 bg-red-950/20 p-3 rounded" data-testid="register-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary w-full justify-center mt-6"
              data-testid="register-submit-button"
            >
              {submitting ? "Creating..." : "Create account"} <ArrowRight size={16} />
            </button>
          </form>

          <p className="text-sm text-zinc-500 mt-8">
            Already have an account?{" "}
            <Link to="/login" className="text-white hover:underline" data-testid="register-to-login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      <div
        className="hidden md:block relative order-1 md:order-2"
        style={{ backgroundImage: `url('${TEXTURE}')`, backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/80" />
        <div className="absolute bottom-10 left-10 right-10">
          <div className="overline mb-3">14 / Brandscaling</div>
          <p className="font-display text-2xl text-white max-w-sm leading-tight">
            One payment. Lifetime access. 14 days from now you'll be in market.
          </p>
        </div>
      </div>
    </div>
  );
}
